import { useTranslation } from "react-i18next";

import { UserAuthenticatedLayout } from "@/layouts/AuthenticatedLayout/UserAuthenticatedLayout";
import { authenticatedRouting } from "@/modules/router/routings/authenticatedRouting";
import { useGetTasksQuery } from "@/modules/tasks/components/hooks/useGetTasksQuery";
import { TaskItem } from "@/modules/tasks/components/TaskItem/TaskItem";
import { TaskItemsHeader } from "@/modules/tasks/components/TaskItemsHeader/TaskItemsHeader";
import { FirstLoadingPageView } from "@/modules/theme/components/FirstLoadingPageView";
import { Loader } from "@/modules/theme/components/Loader/Loader";
import { PageMeta } from "@/modules/theme/components/PageMeta";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";

import { EmptyTasksView } from "./components/EmptyTasksView";
import { createTasksPageStyles } from "./createTasksPageStyles";

const translationNamespace = "pages/TasksPage";

export const TasksPage = () => {
  const classes = createTasksPageStyles(useTheme());
  const { t } = useTranslation(translationNamespace);

  const {
    data: tasks,
    isLoading: isTasksDataLoading,
    isSuccess: isTasksDataFetched,
  } = useGetTasksQuery();

  const isTasksListEmpty = !tasks?.length && isTasksDataFetched;

  if (isTasksListEmpty) {
    return <EmptyTasksView />;
  }

  if (isTasksDataLoading) {
    return <FirstLoadingPageView heading={t("Tasks")} />;
  }

  const toDoTasks = tasks?.filter((task) => task.progress < 100);
  const doneTasks = tasks?.filter((task) => task.progress === 100);

  return (
    <UserAuthenticatedLayout containerClassName={classes.root}>
      <PageMeta title={t("Tasks")} />
      <div className={classes.content}>
        <Typography className={classes.heading} variant="pageHeading">
          {t("Tasks")}
        </Typography>
        {isTasksDataLoading ? (
          <div className={classes.loaderWrapper}>
            <Loader size={70} variant="primary" />
          </div>
        ) : (
          <>
            {toDoTasks?.length ? (
              <div>
                <TaskItemsHeader>{t("To do")}</TaskItemsHeader>
                {toDoTasks.map((task) => (
                  <TaskItem
                    groups={task.groups}
                    key={task.id}
                    deadline={task.deadline}
                    name={task.name}
                    progress={task.progress}
                    pointsNumber={task.pointsNumber}
                    link={authenticatedRouting.buildUrl("taskDetails", {
                      params: {
                        id: task.id,
                      },
                    })}
                  />
                ))}
              </div>
            ) : null}
            {doneTasks?.length ? (
              <div>
                <TaskItemsHeader>{t("Done")}</TaskItemsHeader>
                {doneTasks?.map((task) => (
                  <TaskItem
                    groups={task.groups}
                    key={task.id}
                    name={task.name}
                    progress={task.progress}
                    done
                    pointsNumber={task.pointsNumber}
                    link={authenticatedRouting.buildUrl("taskDetails", {
                      params: {
                        id: task.id,
                      },
                    })}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
    </UserAuthenticatedLayout>
  );
};
